import { FunctionComponent, PropsWithChildren } from 'react'
import styled from 'styled-components'

import Map, { Result } from '../Map'
import AdventureCallout from './AdventureCallout'

// Clustered leaves all sit at the cluster centre; the map hands each one an
// offset to spread them out once the cluster opens.
const Spread = styled.div`
  transition: transform 300ms ease-out;
`

type MarkerCalloutProps = {
  type: Result['type']
  transform?: { x: number; y: number }
  adventure?: AdventureResult['adventure']
}

const MarkerCallout: FunctionComponent<
  PropsWithChildren<MarkerCalloutProps>
> = ({ type, ...props }) => {
  if (type === 'Adventure' && props.adventure) {
    return <AdventureCallout {...props} />
  }

  switch (type) {
    case 'Adventure':
    case 'Listing':
      return (
        <Spread
          style={{
            transform: `translate(${props.transform?.x ?? 0}px, ${props.transform?.y ?? 0}px)`,
          }}
        >
          {props.children}
        </Spread>
      )
    default:
      return null
  }
}

export type ListingPopupData = {
  id: number | string
  location: {
    lat: number
    lng: number
  }
  listing_type: {
    slug: string
  }
  listing_subcategory?: {
    slug: string
  }
}

type ListingResult = Result & {
  listing: ListingPopupData
}

type AdventureResult = Result & {
  adventure?: Omit<GQL_AdventureCardFragmentFragment, 'url'>
}

type ListingMapProps = {
  listing?: ListingResult
  place?: Result
  stays?: Result[]
  adventures?: AdventureResult[]
  analyticsSource: string
  hide?: boolean
  clusterByType?: boolean
}

const ListingMap: FunctionComponent<ListingMapProps> = ({
  listing,
  place,
  stays,
  adventures,
  analyticsSource,
  hide,
  clusterByType,
}) => {
  if (hide) {
    return null
  }

  const unclustered = [listing, place].filter(Boolean)
  const clustered = [...(stays ?? []), ...(adventures ?? [])]

  return (
    <Map
      unclusteredResults={unclustered}
      results={clustered}
      clusterByType={clusterByType}
      analyticsSource={analyticsSource}
      PopupComponent={MarkerCallout}
      defaultZoom={10}
      noSearchThisArea
    />
  )
}

export default ListingMap
